import {
  ChannelPicker,
  ConnectMailboxAffordance,
  type ChannelPickerRow,
} from '@/components/actions/send-invitation/outreach/channel-picker';
import { useEmailIntegrationStatus } from '@/hooks/use-email-integration-status';
import { Box, Text, VStack } from '@chakra-ui/react';
import {
  DisplayRightsDeliveryChannel,
  DisplayRightsEmailChannelBlocker,
  type DisplayRightsCompositionContextResponse,
} from '@fluenceur/types';
import { Copy, Mail } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

interface DisplayRightsChannelSectionProps {
  value: DisplayRightsDeliveryChannel;
  onChange: (channel: DisplayRightsDeliveryChannel) => void;
  emailChannel: DisplayRightsCompositionContextResponse['emailChannel'];
}

/**
 * How the proposal link reaches the creator: the brand's own connected mailbox,
 * or a link the brand copies and delivers itself.
 *
 * The mailbox row is offered only when the server says it can carry the link —
 * the reason it cannot travels, the creator's address never does. The address
 * shown on the row is the BRAND user's own mailbox, already public to them
 * through the integrations query, so it answers "sent from where?" without
 * disclosing anything new.
 */
export function DisplayRightsChannelSection({
  value,
  onChange,
  emailChannel,
}: DisplayRightsChannelSectionProps) {
  const { t } = useTranslation();
  const { emailIntegration } = useEmailIntegrationStatus();

  const senderEmail = emailIntegration?.senderEmail ?? null;
  const blocked = !emailChannel.available;
  const missingMailbox =
    emailChannel.blocker === DisplayRightsEmailChannelBlocker.NO_CONNECTED_MAILBOX;

  const rows: ChannelPickerRow<DisplayRightsDeliveryChannel>[] = [
    {
      value: DisplayRightsDeliveryChannel.CONNECTED_EMAIL,
      icon: Mail,
      title: t('displayRights.composer.channel.connectedEmail.title'),
      description:
        blocked && emailChannel.blocker
          ? t(`displayRights.composer.channel.blocker.${emailChannel.blocker}`)
          : (senderEmail ?? t('displayRights.composer.channel.connectedEmail.genericDescription')),
      disabled: blocked,
      // Only a missing mailbox is fixable from here; a missing creator address
      // is not something an OAuth redirect can solve.
      trailing: missingMailbox ? (
        <ConnectMailboxAffordance label={t('displayRights.composer.channel.connect')} />
      ) : undefined,
    },
    {
      value: DisplayRightsDeliveryChannel.LINK_COPY,
      icon: Copy,
      title: t('displayRights.composer.channel.linkCopy.title'),
      description: t('displayRights.composer.channel.linkCopy.description'),
    },
  ];

  return (
    <VStack align="stretch" gap="2" data-testid="display-rights-channel-section">
      <Box>
        <Text fontSize="sm" fontWeight="semibold" color="fg">
          {t('displayRights.composer.sendTitle')}
        </Text>
      </Box>
      <ChannelPicker rows={rows} value={value} onChange={onChange} />
    </VStack>
  );
}
